import React, { useState } from "react";
import { Link } from "react-router-dom";
import DrinksImage from "../assets/images/about/drinks.webp";
import GroupImage from "../assets/images/about/group.webp";
import MainImage from "../assets/images/about/main.webp";
import PartyImage from "../assets/images/about/party.webp";
import LogoOutline from "../assets/images/logos/ciao-fridays-badge-outline-white.svg";
import RuleByob from "../assets/images/rules/byob.webp";
import RuleSnacks from "../assets/images/rules/snacks.webp";
import RuleTheme from "../assets/images/rules/theme.webp";
import { useAuth } from "../context/AuthContext";

const PHOTOS = [
  { src: MainImage, caption: "ciao fridays" },
  { src: GroupImage, caption: "friday night" },
  { src: DrinksImage, caption: "byob" },
  { src: PartyImage, caption: "see you friday" },
];

// Resting positions of the stack, front to back
const STACK = [
  { rotation: -4, x: 0, y: 0 },
  { rotation: 5, x: 14, y: -8 },
  { rotation: -9, x: -12, y: 6 },
  { rotation: 8, x: 8, y: -14 },
];

// The poster text is part of the image, so it is repeated here for screen readers
const RULES = [
  {
    src: RuleByob,
    title: "Bring your own bottle",
    alt: "BYOB poster: Bring your favorite drinks. Wine, gin, beer, or whatever gets you in the Friday mood. Ciao Fridays is all about sharing good times, not bar tabs, so stock up and sip what you love while we handle the rest.",
    tilt: "-rotate-2",
  },
  {
    src: RuleSnacks,
    title: "Come hungry, food's on us",
    alt: "Snacks poster: We've got the food covered! From tasty bites to late night nibbles, the Ciao Fridays kitchen has you taken care of. Just show up hungry and ready to mingle, we'll handle the rest.",
    tilt: "rotate-1 md:translate-y-6",
  },
  {
    src: RuleTheme,
    title: "Dress for the theme",
    alt: "Theme poster: Every Ciao Friday comes with a unique theme. It's your chance to play along. Dress up, lean in, and make it your own! Whether it's a beach party or a retro night, the more you commit, the more fun it gets.",
    tilt: "-rotate-1",
  },
];

const Polaroid: React.FC<{
  src: string;
  caption: string;
  depth: number;
  leaving: boolean;
  eager: boolean;
}> = ({ src, caption, depth, leaving, eager }) => {
  const [loaded, setLoaded] = useState(false);
  const pos = STACK[depth];

  return (
    <figure
      aria-hidden={depth !== 0}
      className="absolute bg-[#F4EFE4] p-3 pb-14 transition-[transform,opacity] duration-500 ease-out"
      style={{
        transform: leaving
          ? "translate(110%, -12%) rotate(18deg)"
          : `translate(${pos.x}px, ${pos.y}px) rotate(${pos.rotation}deg)`,
        opacity: leaving ? 0 : 1,
        zIndex: STACK.length - depth,
        boxShadow: "0 1px 0 rgba(27,26,23,.25), 0 18px 40px -18px rgba(27,26,23,.55)",
      }}
    >
      <div className="relative h-60 w-60 overflow-hidden bg-coal sm:h-72 sm:w-72">
        {!loaded && <div className="absolute inset-0 animate-shimmer" />}
        <img
          src={src}
          alt=""
          width={288}
          height={288}
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          draggable={false}
          onLoad={() => setLoaded(true)}
          className={`h-full w-full object-cover transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`}
        />
      </div>
      <figcaption className="absolute inset-x-0 bottom-3 text-center font-handwriting text-2xl text-night/80">
        {caption}
      </figcaption>
    </figure>
  );
};

const Landing: React.FC = () => {
  const { user } = useAuth();
  const [front, setFront] = useState(0);
  const [leaving, setLeaving] = useState(false);

  const shuffle = () => {
    if (leaving) return;
    setLeaving(true);
    setTimeout(() => {
      setFront((i) => (i + 1) % PHOTOS.length);
      setLeaving(false);
    }, 450);
  };

  return (
    <div className="pt-4 md:-mt-8 md:pt-0">
      {/* Hero */}
      <section className="grid min-h-[min(calc(100svh-7rem),52rem)] items-center gap-14 pb-16 lg:grid-cols-[1.45fr_1fr] lg:gap-10">
        <div className="animate-fade-in">
          <h1 className="font-display text-[clamp(2.75rem,7vw,5.5rem)] font-black leading-[0.92] tracking-[-0.035em]">
            Themed house parties on Friday nights, every now and then.
          </h1>
          <p className="mt-7 max-w-[34rem] text-lg leading-relaxed text-muted md:text-xl">
            Hosted at home by Alberto Valdes Rey, Stefano Granata and Eleonora Dussin.
            A new theme every time, a room full of people worth meeting, and no bar tab.
          </p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row">
            <Link to="/events" className="btn btn-accent sm:!w-auto sm:px-8">
              See upcoming parties
            </Link>
            {!user && (
              <Link to="/auth" className="btn btn-secondary sm:!w-auto sm:px-8">
                Create a free account
              </Link>
            )}
          </div>
          <p className="mt-6 flex items-center gap-2 font-handwriting text-2xl text-cream">
            <svg width="44" height="26" viewBox="0 0 44 26" fill="none" aria-hidden="true" className="-scale-x-100 text-gold">
              <path d="M2 4c9 1 22 4 30 13M32 17l-1.5-8M32 17l-8.5-1.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            free. just bring a bottle.
          </p>
        </div>

        <div className="relative flex justify-center">
          <button
            type="button"
            onClick={shuffle}
            aria-label="Show next photo"
            className="relative flex h-[22rem] w-[19rem] items-center justify-center sm:h-[26rem] sm:w-[23rem]"
          >
            {PHOTOS.map((photo, i) => {
              const depth = (i - front + PHOTOS.length) % PHOTOS.length;
              return (
                <Polaroid
                  key={photo.src}
                  src={photo.src}
                  caption={photo.caption}
                  depth={depth}
                  leaving={leaving && depth === 0}
                  eager={i === 0}
                />
              );
            })}
          </button>
          <p className="absolute -bottom-8 right-4 font-handwriting text-xl text-muted sm:right-10" aria-hidden="true">
            tap for more
          </p>
        </div>
      </section>

      {/* House rules */}
      <section className="border-t border-cream/15 py-20" aria-labelledby="rules-heading">
        <div className="max-w-xl">
          <h2 id="rules-heading" className="text-4xl font-bold md:text-5xl">
            Three house rules
          </h2>
          <p className="mt-4 text-lg text-muted">
            That's the whole rulebook. We take care of the food, the music and the space.
          </p>
        </div>
        <ul className="mt-14 grid gap-12 sm:grid-cols-2 md:grid-cols-3 md:gap-8">
          {RULES.map((rule) => (
            <li key={rule.title} className={`relative mx-auto w-full max-w-[22rem] ${rule.tilt}`}>
              <span className="tape" aria-hidden="true" />
              <img
                src={rule.src}
                alt={rule.alt}
                width={900}
                height={1273}
                loading="lazy"
                decoding="async"
                className="block aspect-[900/1273] w-full bg-coal object-cover"
                style={{ boxShadow: "0 18px 36px -20px rgba(27,26,23,.6)" }}
              />
              <p className="mt-4 text-center font-display text-xl font-semibold">{rule.title}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* About */}
      <section className="grid gap-8 border-t border-cream/15 py-20 md:grid-cols-[1fr_1.4fr] md:gap-16" aria-labelledby="about-heading">
        <h2 id="about-heading" className="text-4xl font-bold leading-tight md:text-5xl">
          More than a party
        </h2>
        <div className="max-w-[38rem] space-y-5 text-lg leading-relaxed">
          <p>
            Ciao Fridays started as a way to get through the dark Luxembourg winter together:
            a calmer room than a bar, where you can actually hear each other talk.
          </p>
          <p className="text-muted">
            Every party has its own theme and usually a game or two. Come on your own or bring a
            friend. Most people leave with a few new ones.
          </p>
        </div>
      </section>

      {/* Closing band */}
      <section className="relative -mx-5 overflow-hidden bg-gold px-5 py-16 text-night md:-mx-8 md:rounded-2xl md:px-14 md:py-20">
        <div className="relative z-10 max-w-xl">
          <h2 className="text-4xl font-bold leading-tight md:text-5xl">
            See what's coming up.
          </h2>
          <p className="mt-4 text-lg text-night/75">
            Spots are limited, so sign up early.
          </p>
          <Link
            to="/events"
            className="btn mt-8 !w-auto border-night bg-night px-8 text-cream hover:border-coal hover:bg-coal"
          >
            Save my spot
          </Link>
        </div>
        <img
          src={LogoOutline}
          alt=""
          width={320}
          height={320}
          loading="lazy"
          className="pointer-events-none absolute -bottom-16 -right-16 w-64 rotate-12 opacity-80 invert md:-bottom-10 md:right-10 md:w-80"
        />
      </section>
    </div>
  );
};

export default Landing;
